import { useCallback, useEffect, useState } from 'react';
import api, { NAV_COUNTS_CHANGED } from './useApi';
import useRefreshOnFocus from './useRefreshOnFocus';

// The sidebar badges for pages whose own tabs say something is waiting:
// Benefits' Change Requests, the Approvals queues, Suggestions to review and
// the rest. One read, GET /nav/counts, answers for every such page the caller
// holds, keyed by sidebar page id, each { open, overdue }. A page the caller
// cannot open is simply absent, so it never draws a badge.
//
// Polled every minute and caught up on returning to the tab or changing page.
// Every successful write through the api client announces NAV_COUNTS_CHANGED,
// so approving a request takes its badge down at once; the refresh waits a
// moment after the last one, so a bulk action of twenty writes asks once.
const POLL_INTERVAL = 60000;
const SETTLE_MS = 1500;

export default function useNavCounts({ enabled = true } = {}) {
  const [counts, setCounts] = useState({});

  const refresh = useCallback(() => {
    if (!enabled) return;
    api.get('nav/counts')
      .then(c => setCounts(c && typeof c === 'object' ? c : {}))
      // Non-fatal: a failed poll keeps the last known counts.
      .catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setCounts({}); return undefined; }
    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL);
    let settle = null;
    const onChanged = () => { clearTimeout(settle); settle = setTimeout(refresh, SETTLE_MS); };
    window.addEventListener(NAV_COUNTS_CHANGED, onChanged);
    return () => { clearInterval(timer); clearTimeout(settle); window.removeEventListener(NAV_COUNTS_CHANGED, onChanged); };
  }, [enabled, refresh]);

  useRefreshOnFocus(refresh);

  return { counts, refresh };
}
